import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, ArrowLeft, Loader2, Sun, Moon, CheckCircle } from "lucide-react";
import axios from "axios";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex ${theme === 'dark' ? 'bg-[#0A0E14]' : 'bg-gray-50'}`}>
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-slide-up">
          {/* Back button and Theme Toggle */}
          <div className="flex items-center justify-between">
            <Link 
              to="/"
              className={`inline-flex items-center gap-2 transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={theme === 'dark' 
                ? "https://customer-assets.emergentagent.com/job_be4bb2aa-ca20-4b1a-9d54-431ad1ac86d0/artifacts/ie0vbsv3_primary.Horiz.2line.knockedout.whouter.pms315%2Bwhite.png"
                : "https://customer-assets.emergentagent.com/job_student-journey-17/artifacts/lcfdritp_primary.Horiz.2line.knockedout.pms315.png"
              }
              alt="University of St. Augustine for Health Sciences" 
              className="h-12 w-auto"
            />
          </div>

          {/* Header */}
          <div>
            <h2 className={`font-['Montserrat'] text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Welcome Back</h2>
            <p className={`mt-2 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>Sign in to continue your application</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className={theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Email Address</Label>
              <div className="relative">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-12 h-14 rounded-xl focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] ${
                    theme === 'dark' 
                      ? 'bg-black/20 border-white/10 text-white placeholder:text-slate-600' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                  }`}
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Password</Label>
              <div className="relative">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-12 pr-12 h-14 rounded-xl focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] ${
                    theme === 'dark' 
                      ? 'bg-black/20 border-white/10 text-white placeholder:text-slate-600' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                  }`}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${theme === 'dark' ? 'text-slate-500 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#00677F] hover:text-[#135163] font-medium transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#00677F] hover:bg-[#135163] text-white rounded-xl text-lg font-medium shadow-[0_0_20px_rgba(0,180,216,0.3)] hover:shadow-[0_0_30px_rgba(0,180,216,0.5)] transition-all"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Register link */}
          <p className={`text-center ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
            Don't have an account?{" "}
            <Link 
              to="/register" 
              className="text-[#00677F] hover:text-[#135163] font-medium transition-colors"
              data-testid="register-link"
            >
              Create Account
            </Link>
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/40 backdrop-blur-md'}`}>
          <div className={`backdrop-blur-xl border rounded-2xl p-6 sm:p-8 max-w-md w-full animate-slide-up ${theme === 'dark' ? 'bg-[#11161F] border-white/[0.08]' : 'bg-white border-gray-200 shadow-lg'}`} data-testid="forgot-password-modal">
            {forgotSuccess ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className={`font-['Montserrat'] text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Check Your Email
                </h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  If an account exists for <span className="font-medium text-[#00677F]">{forgotEmail}</span>, we've sent a password reset link.
                </p>
                {resetToken && (
                  <div className={`mt-4 p-3 rounded-xl text-left ${theme === 'dark' ? 'bg-black/30 border border-white/[0.06]' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className={`text-xs uppercase tracking-wider mb-2 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Demo Reset Link</p>
                    <Link
                      to={`/reset-password/${resetToken}`}
                      onClick={() => setShowForgotPassword(false)}
                      className="text-sm text-[#00677F] hover:text-[#135163] underline break-all"
                      data-testid="reset-link"
                    >
                      Click here to reset your password
                    </Link>
                  </div>
                )}
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSuccess(false);
                    setForgotEmail("");
                    setResetToken(null);
                  }}
                  className="w-full bg-[#00677F] hover:bg-[#135163] text-white rounded-xl h-12 mt-2"
                  style={{ color: '#FFFFFF' }}
                  data-testid="close-forgot-modal-btn"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className={`font-['Montserrat'] text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Forgot Password?
                    </h3>
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                      Enter your email and we'll send you a reset link.
                    </p>
                  </div>
                  <button
                    onClick={() => { setShowForgotPassword(false); setForgotEmail(""); }}
                    className={`p-1 rounded-lg transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                    data-testid="close-forgot-btn"
                  >
                    <span className="text-xl">&times;</span>
                  </button>
                </div>
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="space-y-2">
                    <Label className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>Email Address</Label>
                    <div className="relative">
                      <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-400'}`} />
                      <Input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="Enter your email"
                        className={`pl-12 h-14 rounded-xl text-base ${theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white placeholder:text-slate-600 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#00677F] focus:ring-2 focus:ring-[#00677F]/20'}`}
                        data-testid="forgot-email-input"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full h-14 bg-[#00677F] hover:bg-[#135163] text-white rounded-xl text-lg font-medium shadow-[0_0_20px_rgba(0,180,216,0.3)]"
                    style={{ color: '#FFFFFF' }}
                    data-testid="send-reset-link-btn"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Right side - Visual */}
      <div className={`hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden ${theme === 'dark' ? '' : 'bg-gradient-to-br from-[#E0F7FA] to-[#B2EBF2]'}`}>
        <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-br from-[#002855]/50 to-[#0A0E14]' : ''}`} />
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-[100px] ${theme === 'dark' ? 'bg-[#00677F]/10' : 'bg-[#00677F]/20'}`} />
        <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-[100px] ${theme === 'dark' ? 'bg-[#7B68EE]/10' : 'bg-[#7B68EE]/15'}`} />
        
        <div className="relative z-10 text-center max-w-lg">
          <img 
            src="https://customer-assets.emergentagent.com/job_be4bb2aa-ca20-4b1a-9d54-431ad1ac86d0/artifacts/7kvxdoxy_primary.filled.whouter.pms315.png" 
            alt="USA.edu Logo" 
            className="h-24 w-auto mx-auto mb-8"
          />
          <h2 className={`font-['Montserrat'] text-4xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-[#002855]'}`}>
            University of St. Augustine
          </h2>
          <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-[#004D6E]'}`}>
            For Health Sciences
          </p>
          <div className="mt-12 flex justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-['Montserrat'] font-bold text-[#00677F]">15K+</p>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-[#004D6E]'}`}>Students</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-['Montserrat'] font-bold text-[#00677F]">5</p>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-[#004D6E]'}`}>Campuses</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-['Montserrat'] font-bold text-[#00677F]">95%</p>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-[#004D6E]'}`}>Employment</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
